import React, { useState } from "react";
import { FiDroplet, FiDollarSign, FiTrendingDown, FiCpu, FiClock, FiCalendar, FiList } from "react-icons/fi";
import { MiniBar } from "./fleetHelpers";

// Efficiency colour: green ≥ 4.5, yellow ≥ 3.5, red < 3.5
const effColor = (v) => {
  if (v === 0)  return "#94a3b8";
  if (v >= 4.5) return "#10b981";
  if (v >= 3.5) return "#f59e0b";
  return "#ef4444";
};

const VIEWS = [
  { id: "today",   label: "Today",     icon: FiClock    },
  { id: "weekly",  label: "This Week", icon: FiList     },
  { id: "monthly", label: "Monthly",   icon: FiCalendar },
];

const FuelCostPanel = ({ d }) => {
  const f = d.fuel;
  const [view, setView]     = useState("today");
  const [sortBy, setSortBy] = useState("litres");

  // Sorted vehicle fuel table
  const sortedVehicles = [...f.vehicleFuel].sort((a, b) => {
    if (sortBy === "efficiency") {
      if (a.efficiency === 0 && b.efficiency === 0) return 0;
      if (a.efficiency === 0) return 1;
      if (b.efficiency === 0) return -1;
      return b.efficiency - a.efficiency;
    }
    return b[sortBy] - a[sortBy];
  });

  const maxVehicleLitres = Math.max(...f.vehicleFuel.map(v => v.litres));
  const maxHourly  = Math.max(...f.hourlyToday.map(h => h.litres));
  const maxWeekly  = Math.max(...f.weeklyTrend.map(w => w.litres));
  const maxMonthly = Math.max(...f.monthlyTrend.map(m => m.litres));

  return (
    <div className="fleet-fuel">

      {/* ── Summary KPI Row ── */}
      <div className="fc-summary-row">
        <div className="fc-sum-card">
          <div className="fc-sum-icon" style={{ background: "#fef3c7", color: "#f59e0b" }}>
            <FiDroplet size={20} />
          </div>
          <div className="fc-sum-content">
            <div className="fc-sum-val">{f.todayTotal.toLocaleString()} L</div>
            <div className="fc-sum-label">Fuel Today</div>
            <div className="fc-sum-sub">Avg {f.avgEfficiency} km/L</div>
          </div>
        </div>

        <div className="fc-sum-card">
          <div className="fc-sum-icon" style={{ background: "#fce7f3", color: "#ec4899" }}>
            <FiDollarSign size={20} />
          </div>
          <div className="fc-sum-content">
            <div className="fc-sum-val">₹{(d.summary.costToday / 1000).toFixed(1)}k</div>
            <div className="fc-sum-label">Cost Today</div>
            <div className="fc-sum-sub">₹{f.costPerKm}/km</div>
          </div>
        </div>

        <div className="fc-sum-card">
          <div className="fc-sum-icon" style={{ background: "#fee2e2", color: "#ef4444" }}>
            <FiTrendingDown size={20} />
          </div>
          <div className="fc-sum-content">
            <div className="fc-sum-val">{f.idlingLoss} L</div>
            <div className="fc-sum-label">Idle Waste Today</div>
            <div className="fc-sum-sub">₹{f.idlingCost} wasted</div>
          </div>
        </div>

        <div className="fc-sum-card">
          <div className="fc-sum-icon" style={{ background: "#ede9fe", color: "#8b5cf6" }}>
            <FiCpu size={20} />
          </div>
          <div className="fc-sum-content">
            <div className="fc-sum-val">₹{(d.summary.costMonth / 1000).toFixed(0)}k</div>
            <div className="fc-sum-label">Monthly Cost</div>
            <div className="fc-sum-sub">Current month</div>
          </div>
        </div>
      </div>

      {/* ── View Toggle + AI strip ── */}
      <div className="fc-view-row">
        <div className="fc-view-toggle">
          {VIEWS.map(v => (
            <button
              key={v.id}
              className={`fc-view-btn ${view === v.id ? "fc-view-active" : ""}`}
              onClick={() => setView(v.id)}
            >
              <v.icon size={13} />
              {v.label}
            </button>
          ))}
        </div>
        <div className="fc-ai-strip">
          <FiCpu size={13} color="#667eea" />
          <span>{f.aiSuggestions[0]}</span>
        </div>
      </div>

      {/* ── History Chart (switches by view) ── */}
      {view === "today" && (
        <div className="fc-chart-card">
          <div className="fc-card-title"><FiClock size={14} /> Hourly Fuel Consumption – Today</div>
          <div className="fc-bar-chart fc-bar-chart-hourly">
            {f.hourlyToday.map(h => (
              <div key={h.h} className="fc-bar-col">
                <div className="fc-bar-label-top" style={{ visibility: h.litres > 0 ? "visible" : "hidden" }}>
                  {h.litres} L
                </div>
                <div className="fc-bar-wrap">
                  <div
                    className="fc-bar"
                    style={{
                      height: `${maxHourly > 0 ? (h.litres / maxHourly) * 100 : 0}%`,
                      background: h.litres === 0 ? "#e2e8f0" : "#667eea",
                    }}
                  />
                </div>
                <span className="fc-bar-day" style={{ fontSize: "0.6rem" }}>{h.h}</span>
                {h.litres > 0 && <span className="fc-bar-cost-label">₹{(h.cost / 1000).toFixed(1)}k</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "weekly" && (
        <div className="fc-chart-card">
          <div className="fc-card-title"><FiList size={14} /> Weekly Fuel Consumption – This Week</div>
          <div className="fc-bar-chart">
            {f.weeklyTrend.map(day => (
              <div key={day.d} className="fc-bar-col">
                <div className="fc-bar-label-top">{(day.litres / 1000).toFixed(1)}k L</div>
                <div className="fc-bar-wrap">
                  <div className="fc-bar" style={{ height: `${(day.litres / maxWeekly) * 100}%`, background: "#667eea" }}>
                    <div className="fc-bar-cost" style={{ height: "40%", background: "#ec4899", opacity: 0.5 }} />
                  </div>
                </div>
                <span className="fc-bar-day">{day.d}</span>
                <span className="fc-bar-cost-label">₹{(day.cost / 1000).toFixed(1)}k</span>
              </div>
            ))}
          </div>
          <div className="fc-chart-legend">
            <span><span className="fc-legend-dot" style={{ background: "#667eea" }} /> Fuel (litres)</span>
            <span><span className="fc-legend-dot" style={{ background: "#ec4899" }} /> Cost (₹)</span>
          </div>
        </div>
      )}

      {view === "monthly" && (
        <div className="fc-chart-card">
          <div className="fc-card-title"><FiCalendar size={14} /> Monthly Fuel Trend – Last 5 Weeks</div>
          <div className="fc-monthly-chart">
            {f.monthlyTrend.map((w, i) => (
              <div key={i} className="fc-monthly-row">
                <div className="fc-monthly-label">{w.week}</div>
                <div className="fc-monthly-bar-wrap">
                  <div
                    className="fc-monthly-bar"
                    style={{ width: `${(w.litres / maxMonthly) * 100}%` }}
                  >
                    <span className="fc-monthly-bar-val">{(w.litres / 1000).toFixed(1)}k L</span>
                  </div>
                </div>
                <div className="fc-monthly-stats">
                  <span className="fc-monthly-cost">₹{(w.cost / 1000).toFixed(0)}k</span>
                  <span className="fc-monthly-eff" style={{ color: effColor(w.efficiency) }}>{w.efficiency} km/L</span>
                  <span className="fc-monthly-trips">{w.trips} trips</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Vehicle Fuel Table + AI suggestions ── */}
      <div className="fc-bottom-row">

        {/* Per-vehicle table */}
        <div className="fc-vehicle-table-card">
          <div className="fc-card-title">
            <FiDroplet size={14} /> Vehicle Fuel Usage – Today
            <div className="fc-sort-row">
              <span className="fc-sort-label">Sort by:</span>
              {[
                { key: "litres",     label: "Fuel"       },
                { key: "cost",       label: "Cost"       },
                { key: "efficiency", label: "Efficiency" },
                { key: "kmDriven",   label: "KM"         },
              ].map(s => (
                <button
                  key={s.key}
                  className={`fc-sort-btn ${sortBy === s.key ? "fc-sort-active" : ""}`}
                  onClick={() => setSortBy(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="fc-veh-table-wrap">
            <table className="fc-veh-table">
              <thead>
                <tr>
                  <th className="fc-vth">#</th>
                  <th className="fc-vth">Vehicle</th>
                  <th className="fc-vth">Driver</th>
                  <th className="fc-vth">Fuel Used</th>
                  <th className="fc-vth">Cost</th>
                  <th className="fc-vth">Efficiency</th>
                  <th className="fc-vth">KM Driven</th>
                  <th className="fc-vth">Idle Waste</th>
                  <th className="fc-vth" style={{ minWidth: 120 }}>Fuel Bar</th>
                </tr>
              </thead>
              <tbody>
                {sortedVehicles.map((v, i) => (
                  <tr key={v.vehicleId} className={`fc-vtr ${v.litres === 0 ? "fc-vtr-zero" : ""}`}>
                    <td className="fc-vtd fc-vrank">{i + 1}</td>
                    <td className="fc-vtd">
                      <div className="fc-v-name">{v.name}</div>
                      <div className="fc-v-sub">{v.vehicleId} · {v.type}</div>
                    </td>
                    <td className="fc-vtd fc-v-driver">{v.driver}</td>
                    <td className="fc-vtd">
                      <span className="fc-v-litres" style={{
                        color: v.litres > 150 ? "#ef4444" : v.litres > 80 ? "#f59e0b" : v.litres > 0 ? "#1e293b" : "#94a3b8"
                      }}>
                        {v.litres > 0 ? `${v.litres} L` : "—"}
                      </span>
                    </td>
                    <td className="fc-vtd fc-v-cost">
                      {v.cost > 0 ? `₹${v.cost.toLocaleString()}` : "—"}
                    </td>
                    <td className="fc-vtd">
                      <span className="fc-v-eff" style={{ color: effColor(v.efficiency) }}>
                        {v.efficiency > 0 ? `${v.efficiency} km/L` : "—"}
                      </span>
                    </td>
                    <td className="fc-vtd fc-v-km">
                      {v.kmDriven > 0 ? `${v.kmDriven} km` : "—"}
                    </td>
                    <td className="fc-vtd">
                      {v.idleLitres > 0
                        ? <span className="fc-idle-val" style={{ color: v.idleLitres > 30 ? "#ef4444" : "#f59e0b" }}>
                            {v.idleLitres} L
                          </span>
                        : <span className="fc-idle-val" style={{ color: "#94a3b8" }}>—</span>
                      }
                    </td>
                    <td className="fc-vtd">
                      <MiniBar
                        value={v.litres}
                        max={maxVehicleLitres}
                        color={v.litres > 150 ? "#ef4444" : v.litres > 80 ? "#f59e0b" : "#667eea"}
                        height={7}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI Suggestions */}
        <div className="fc-ai-card">
          <div className="fc-card-title"><FiCpu size={14} /> AI Fuel Optimization</div>
          <div className="fc-ai-list">
            {f.aiSuggestions.map((s, i) => (
              <div key={i} className="fc-ai-item">
                <div className="fc-ai-bullet">💡</div>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default FuelCostPanel;
